import React from 'react';
import { useTranslation } from 'react-i18next';
import { AllowIcon, BlockIcon } from 'shared/components/ui/ProjectIcons';
import { SourceFilter } from './sourceFilterTypes';

// The one-glance summary of a device rule: mode pill, Paused, and the two-sided
// sentence. Shared by the flow-map card and the rules panel's list row — the two
// copies had already drifted once.

type DeviceRuleSummaryProps = {
  filter: SourceFilter;
  destinationName: string;
};

const DeviceRuleSummary = ({
  filter,
  destinationName,
}: DeviceRuleSummaryProps) => {
  const { t } = useTranslation();
  const blocking = filter.mode === 'blacklist';
  return (
    <div className="min-w-0" data-testid="DeviceRuleSummary">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-body-sm font-bold ${
            blocking
              ? 'bg-ui-status-bg-pending-sync-bg text-ui-status-disabled'
              : 'bg-teal-t10 text-green-1'
          }`}
        >
          {blocking ? (
            <>
              <BlockIcon size={12} /> {t('Blocked sources')}
            </>
          ) : (
            <>
              <AllowIcon size={12} /> {t('Allowed sources')}
            </>
          )}
        </span>
        {!filter.enabled && (
          <span className="rounded bg-greyscale-11 px-2 py-0.5 text-body-sm font-bold text-ui-text-secondary-text">
            {t('Paused')}
          </span>
        )}
      </div>
      {/* Both halves of the rule, since the count alone doesn't say what happens
                to everything NOT on the list. */}
      <div className="break-words text-body-sm text-ui-text-primary-text">
        {blocking
          ? t(
              '{{count}} listed sources are blocked from {{name}} — everything else gets through.',
              { count: filter.sources_count, name: destinationName },
            )
          : t(
              'Only {{count}} listed sources reach {{name}} — everything else is blocked.',
              { count: filter.sources_count, name: destinationName },
            )}
      </div>
      {/* The rule's scope. A rule only affects the providers it names, and on a
                multi-provider route the device list alone gives no clue why another
                feed is untouched. */}
      {(filter.providers?.length ?? 0) > 0 && (
        <div
          className="mt-1 break-words text-body-sm text-ui-text-secondary-text"
          data-testid="DeviceRuleSummary-providers"
        >
          {t('Applies to sources from {{providers}}.', {
            providers: filter.providers
              .map((provider) => provider.name)
              .join(', '),
          })}
        </div>
      )}
    </div>
  );
};

export default DeviceRuleSummary;
